import React from "react";
import Col from "react-bootstrap/Col";
import ProjectNameDisplay from "./ProjectNameDisplay.jsx";
import StatusDropdown from "./StatusDropdown.jsx";
import {
  renderProjectStatusDropdown,
  renderProjectSearchInput,
  renderClientList,
} from "../utils/cardBodyRenderHelpers.js";
import { CSS_CLASSES, INLINE_STYLES, MESSAGES, STATUS_OPTIONS, DEFAULT_VALUES, PLACEHOLDERS } from "../utils/cardBodyConstants.js";
import { isEntryUnlocked } from "../utils/cardBodyDateHelpers.js";
import userTypeConsts from "../../../../../constants/userTypeConsts.js";
const TopArrowIcon = () => (
  <svg width="7" height="4" viewBox="0 0 7 4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3.5L3.5 1L6 3.5" stroke="#405BFF" strokeWidth="0.866932"/>
  </svg>
);
const DownArrowIcon = () => (
  <svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 1L3.5 3.5L1 1" stroke="#405BFF" strokeWidth="0.866932"/>
  </svg>
);

/**
 * ProjectDropdown Component
 * Reusable component for project dropdown in edit and view modes
 */
const ProjectDropdown = ({
  // Mode props
  isEditMode,
  isMergedMode,
  isRowMode = false, // For merged entries rows
  
  // Display props
  projectNameEdit,
  sprintNameEdit,
  clientEdit,
  projectIdEdit,
  projectName,
  sprintName,
  clientName,
  
  // Dropdown state
  projectEditDropdownOpen,
  setProjectEditDropdownOpen,
  projectStatusFilter,
  setProjectStatusFilter,
  filter,
  setFilter,
  selectedClients,
  setSelectedClients,
  selectedProject,
  setSelectedProject,
  
  // Status dropdown state (varies by location)
  isProjectStatusDropdownOpen,
  setIsProjectStatusDropdownOpen,
  projectStatusDropdownRef,
  isProjectStatusMergeDropdownOpen,
  setIsProjectStatusMergeDropdownOpen,
  projectStatusMergeDropdownRef,
  
  // Handlers
  handleProjectStatusChange,
  setTagEditDropdownOpen,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  setClickId,
  group,
  row,
  
  // Data
  user,
  filteredClientData,
  
  // Setters
  setSprintIdEdit,
  setSprintNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  
  // Handlers
  createProjectSelectHandler,
  createSprintSelectHandler,
  
  // Refs
  projectEditDropdownRef,
  projectDropdownContentRef,
  
  // Additional props
  className = "",
  style = {},
  onColClick,
  onColClickWithEvent,
  getLockHoursHandler,
  globalConfig,
}) => {
  const handleDropdownToggle = () => {
    if (onColClick) {
      onColClick();
    } else {
      if (setClickId && group) {
        setClickId(group.entries.length === 1 ? group._id : null);
      }
      setProjectEditDropdownOpen(!projectEditDropdownOpen);
      setProjectStatusFilter(STATUS_OPTIONS.ACTIVE);
      setFilter("");
      setSelectedClients(new Set());
      setSelectedProject(null);
      if (setTagEditDropdownOpen) {
        setTagEditDropdownOpen(false);
      }
      if (setUStartTimeError) setUStartTimeError(false);
      if (setUEndTimeError) setUEndTimeError(false);
      if (setUFormatedTimeError) setUFormatedTimeError(false);
    }
  };

  const handleMobileDropdownToggle = () => {
    setProjectEditDropdownOpen(!projectEditDropdownOpen);
    setProjectStatusFilter(STATUS_OPTIONS.ACTIVE);
    setFilter("");
    setSelectedClients(new Set());
    setSelectedProject(null);
    if (setTagEditDropdownOpen) {
      setTagEditDropdownOpen(false);
    }
  };

  const handleRowDropdownToggle = (e) => {
    if (projectDropdownContentRef?.current && projectDropdownContentRef.current.contains(e.target)) {
      return;
    }
    if (onColClickWithEvent) {
      onColClickWithEvent(e);
    } else if (row && isEntryUnlocked) {
      const isUnlocked = isEntryUnlocked(row.endTime, globalConfig, getLockHoursHandler);
      if (isUnlocked) {
        setProjectEditDropdownOpen(!projectEditDropdownOpen);
        setProjectStatusFilter(STATUS_OPTIONS.ACTIVE);
        setFilter("");
        setSelectedClients(new Set());
        setSelectedProject(null);
        if (setTagEditDropdownOpen) {
          setTagEditDropdownOpen(false);
        }
      }
    }
  };

  // Render edit mode display for single entry
  if (isEditMode && !isMergedMode && !isRowMode) {
    const toggleHandler = className.includes("mobile") ? handleMobileDropdownToggle : handleDropdownToggle;
    return (
      <div className={`project-sprint-outer col ${className}`} ref={projectEditDropdownRef} style={style}>
        <Col className={CSS_CLASSES.CURSOR} onClick={toggleHandler}>
          <ProjectNameDisplay
            projectName={projectNameEdit}
            sprintName={sprintNameEdit}
            clientName={clientEdit}
            projectId={projectIdEdit}
            style={style}
          />
        </Col>
        {projectEditDropdownOpen && (
          <div
            className="custom-dropdown new-active-inactive"
            ref={projectDropdownContentRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {renderProjectStatusDropdown({
              user,
              label: MESSAGES.FILTER_BY_STATUS,
              statusFilter: projectStatusFilter,
              setStatusFilter: handleProjectStatusChange,
              isDropdownOpen: isProjectStatusDropdownOpen,
              setIsDropdownOpen: setIsProjectStatusDropdownOpen,
              dropdownRef: projectStatusDropdownRef,
            })}
            {renderProjectSearchInput({
              filter,
              setFilter,
              projectStatusFilter,
              filteredClientData,
              setSelectedClients,
            })}
            {renderClientList({
              filteredClientData,
              filter,
              projectStatusFilter,
              selectedClients,
              setSelectedClients,
              selectedProject,
              setSelectedProject,
              setSprintIdEdit,
              setSprintNameEdit,
              setProjectIdEdit,
              setProjectNameEdit,
              setClientEdit,
              setClientIdEdit,
              setBillableEdit,
              setProjectEditDropdownOpen,
              user,
              setClickId: setClickId || null,
              group: group || null,
              createProjectSelectHandler: createProjectSelectHandler || null,
              createSprintSelectHandler: createSprintSelectHandler || null,
            })}
          </div>
        )}
      </div>
    );
  }

  // Render edit mode display for merged entry rows
  if (isEditMode && isRowMode && row) {
    return (
      <div className={`project-sprint-outer col ${className}`} ref={projectEditDropdownRef} style={style}>
        <Col className={CSS_CLASSES.CURSOR} onClick={handleRowDropdownToggle}>
          <ProjectNameDisplay
            projectName={projectNameEdit}
            sprintName={sprintNameEdit}
            clientName={clientEdit}
            projectId={projectIdEdit}
          />
        </Col>
        {projectEditDropdownOpen && (
          <div
            className="custom-dropdown new-active-inactive"
            ref={projectDropdownContentRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {(user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) && (
              <StatusDropdown
                label={MESSAGES.FILTER_BY_STATUS}
                statusFilter={projectStatusFilter || DEFAULT_VALUES.PROJECT_STATUS_FILTER}
                setStatusFilter={handleProjectStatusChange}
                isDropdownOpen={isProjectStatusMergeDropdownOpen}
                setIsDropdownOpen={setIsProjectStatusMergeDropdownOpen}
                dropdownRef={projectStatusMergeDropdownRef}
              />
            )}
            <div className="top-search-tracker-for">
              <input
                type="text"
                className="search-bar"
                placeholder={PLACEHOLDERS.FIND_PROJECT_OR_CLIENT}
                value={filter}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const filterValue = e.target.value;
                  setFilter(filterValue);
                  if (filterValue === "") {
                    setSelectedClients(new Set());
                  } else {
                    const matchingClients = filteredClientData.filter((client) =>
                      client.projects.some(
                        (project) =>
                          project.project_name.toLowerCase().includes(filterValue.toLowerCase()) &&
                          (projectStatusFilter === STATUS_OPTIONS.ALL
                            ? true
                            : projectStatusFilter === STATUS_OPTIONS.ACTIVE
                              ? project.is_active === true
                              : project.is_active === false)
                      )
                    );
                    setSelectedClients(new Set(matchingClients));
                  }
                }}
                autoComplete="off"
              />
            </div>
            <ul
              className={`tracker-who-list client-list client-list-filter ${
                user.userType !== userTypeConsts.SUPER_ADMIN && user.userType !== userTypeConsts.ADMIN
                  ? "filter-active-no"
                  : ""
              }`}
            >
              {filteredClientData.map((client) => {
                const matchingProjects = client.projects.filter(
                  (project) =>
                    project.project_name.toLowerCase().includes(filter.toLowerCase()) &&
                    (projectStatusFilter === STATUS_OPTIONS.ALL
                      ? true
                      : projectStatusFilter === STATUS_OPTIONS.ACTIVE
                        ? project.is_active === true
                        : project.is_active === false)
                );

                return (
                  <div key={client._id} className="main-outer-item-dd">
                    <div
                      className={`client-name1 ${selectedClients.has(client) ? "selected" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (matchingProjects.length > 0) {
                          const newSelectedClients = new Set(selectedClients);
                          if (selectedClients.has(client)) {
                            newSelectedClients.delete(client);
                          } else {
                            newSelectedClients.add(client);
                          }
                          setSelectedClients(newSelectedClients);
                        }
                      }}
                    >
                      <span className="main-project">{client.client_name} </span>
                      {matchingProjects.length > 0 && (
                        <span className={`arrow ${selectedClients.has(client) ? "open" : ""}`}>
                          <span className="main-project-title">
                            {matchingProjects.length} {MESSAGES.PROJECTS}
                          </span>
                          {selectedClients.has(client) ? (
                            <TopArrowIcon />
                          ) : (
                            <DownArrowIcon />
                          )}
                        </span>
                      )}
                    </div>
                    {selectedClients.has(client) && (
                      <div className="project-name1-outer">
                        {matchingProjects.length === 0 ? (
                          <div> {MESSAGES.NO_PROJECTS_UNDER_CLIENT} </div>
                        ) : (
                          matchingProjects
                            .sort((a, b) => a.project_name.localeCompare(b.project_name))
                            .map((project) => (
                              <div key={project._id}>
                                <div className={`project-name1 ${selectedProject === project ? "selected" : ""}`}>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSprintIdEdit("");
                                      setSprintNameEdit("");
                                      setProjectIdEdit(project._id);
                                      setProjectNameEdit(project.project_name);
                                      setClientEdit(client.client_name);
                                      setClientIdEdit(client._id);
                                      setBillableEdit(project.is_billable);
                                      setProjectEditDropdownOpen(false);
                                    }}
                                  >
                                    <span className="project-dd-title">{project.project_name} </span>
                                  </div>
                                  <div>
                                    {project.sprints.length > 0 && (
                                      <span
                                        className={`arrow ${selectedProject === project ? "open" : ""}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedProject(selectedProject === project ? null : project);
                                        }}
                                      >
                                        <span className="main-project-title">
                                          {project.sprints.length} {MESSAGES.SPRINTS}
                                        </span>
                                        {selectedProject === project ? (
                                          <TopArrowIcon />
                                        ) : (
                                          <DownArrowIcon />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {selectedProject === project && (
                                  <div>
                                    {project.sprints.length === 0 ? (
                                      <div> {MESSAGES.NO_SPRINTS_UNDER_PROJECT} </div>
                                    ) : (
                                      <ul className="sprint-list">
                                        {project.sprints
                                          .sort((a, b) => a.sprint_name.localeCompare(b.sprint_name))
                                          .map((sprint) => (
                                            <li
                                              key={sprint._id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSprintIdEdit(sprint._id);
                                                setSprintNameEdit(sprint.sprint_name);
                                                setProjectIdEdit(project._id);
                                                setProjectNameEdit(project.project_name);
                                                setClientEdit(client.client_name);
                                                setClientIdEdit(client._id);
                                                setBillableEdit(project.is_billable);
                                                setProjectEditDropdownOpen(false);
                                              }}
                                            >
                                              {sprint.sprint_name}
                                            </li>
                                          ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Render view mode display
  if (!isEditMode) {
    // For row mode, use onColClickWithEvent if provided, otherwise use onColClick
    const handleViewModeClick = isRowMode && onColClickWithEvent 
      ? (e) => {
          if (projectDropdownContentRef?.current && projectDropdownContentRef.current.contains(e.target)) {
            return;
          }
          if (onColClick) {
            onColClick();
          }
        }
      : onColClick;
    
    return (
      <div className={`project-sprint-outer col ${className}`} ref={projectEditDropdownRef} style={style}>
        <Col className={CSS_CLASSES.CURSOR} onClick={handleViewModeClick}>
          <ProjectNameDisplay
            projectName={projectName}
            sprintName={sprintName}
            clientName={clientName}
            showHiddenInput={false}
            style={style}
          />
        </Col>
      </div>
    );
  }

  // Render merged mode display (read-only)
  if (isMergedMode) {
    return (
      <div className={`project-sprint-outer col ${className}`} ref={projectEditDropdownRef} style={style}>
        <Col className={CSS_CLASSES.CURSOR} onClick={onColClick}>
          <ProjectNameDisplay
            projectName={projectName}
            sprintName={sprintName}
            clientName={clientName}
            showHiddenInput={false}
            style={style}
          />
        </Col>
      </div>
    );
  }

  return null;
};

export default ProjectDropdown;
